import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import noResultsIllustration from "../../assets/rewamp/search/no-results.svg";

function SearchNoResults({ message, size, sx, icon, textColor, translate, variant, title, description, option, action }) {
  const { t } = useTranslation();

  if (variant === "panel") {
    return (
      <Paper variant="emptyStateCard" role="status" sx={sx}>
        <Box component="img" src={icon} alt="" aria-hidden="true" sx={{ width: size, height: size, flexShrink: 0 }} />
        {title ? (
          <Typography variant="emptyStateCardTitle" component="p" sx={{ color: "neutral.headings" }}>
            {title}
          </Typography>
        ) : null}
        {description ? (
          <Typography variant="emptyStateCardDesc" component="p" sx={{ color: "neutral.body" }}>
            {description}
          </Typography>
        ) : null}
        {action}
      </Paper>
    );
  }

  if (variant === "card") {
    return (
      <Paper variant="emptyStatePanel" role="status" sx={sx}>
        <Box component="img" src={icon} alt="" aria-hidden="true" sx={{ width: size, height: size, flexShrink: 0 }} />
        {title ? (
          <Typography variant="emptyStateTitle" component="p" sx={{ color: "neutral.headings" }}>
            {title}
          </Typography>
        ) : null}
        {description ? (
          <Typography variant="emptyStateDesc" component="p" sx={{ color: "neutral.body" }}>
            {description}
          </Typography>
        ) : null}
        {option ? <Paper variant="emptyStateOption">{option}</Paper> : null}
      </Paper>
    );
  }

  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5, // 4px — Figma 836:15324
        p: 2, // 16px — Figma dropdown padding (836:15323)
        textAlign: "center",
        ...sx,
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        aria-hidden="true"
        sx={{ width: size, height: size, flexShrink: 0 }}
      />
      <Typography variant="body2" sx={{ color: textColor }}>
        {translate ? t(message) : message}
      </Typography>
    </Box>
  );
}

SearchNoResults.propTypes = {
  /** i18n key (or literal) for the empty-state message. Defaults to the Figma copy. */
  message: PropTypes.string,
  /** Illustration edge length; a number is treated as px. */
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Extra styles merged onto the wrapper. */
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Illustration asset — override for a caller with its own Figma-specific empty-state art (e.g. scheme search, 639-24516). */
  icon: PropTypes.string,
  /** Theme color token for the message text. */
  textColor: PropTypes.string,
  /** false = message is an already-resolved string, not an i18n key. */
  translate: PropTypes.bool,
  /** "card" = dropdown-panel chrome: icon, title, description, optional option container. */
  variant: PropTypes.oneOf(["default", "card", "panel"]),
  /** "panel" = standalone bordered card: icon, title, description. */
  /** card/panel only — bold heading line. */
  title: PropTypes.node,
  /** card only — regular supporting line. */
  description: PropTypes.node,
  /** card only — content for the inner tinted container (e.g. call-centre label + RewampContactPill). */
  option: PropTypes.node,
  /** panel only — call to action below the text (e.g. a primary Button). */
  action: PropTypes.node,
};

SearchNoResults.defaultProps = {
  message: "NO_DATA_FOUND",
  size: 88,
  sx: undefined,
  icon: noResultsIllustration,
  textColor: "text.primary",
  translate: true,
  variant: "default",
  title: undefined,
  description: undefined,
  option: undefined,
  action: undefined,
};

export default SearchNoResults;
